import { useEffect, useState, type ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Props {
  title: string
  /** Résumé lu quand la section est fermée : l'état reste visible sans être modifiable. */
  summary?: ReactNode
  defaultOpen?: boolean
  /**
   * Force l'ouverture — passé à `true` quand la section contient une erreur
   * de validation. Voir la note sur le démontage dans le corps du composant.
   */
  forceOpen?: boolean
  children: ReactNode
}

/**
 * Section repliée par défaut.
 *
 * Sert à séparer ce que le client doit LIRE (le résultat) de ce qui décrit
 * COMMENT c'est fabriqué (nom interne, consignes au modèle, syntaxe des
 * variables). Un champ affiché est une question implicite : replier ce qui
 * est déjà juste évite de faire vérifier ce qui n'a pas à l'être.
 *
 * `summary` garde l'essentiel visible fermé, pour ne pas cacher une décision
 * prise à la place du client.
 */
export function CollapsibleSection({
  title,
  summary,
  defaultOpen = false,
  forceOpen = false,
  children,
}: Props) {
  const [open, setOpen] = useState(defaultOpen)

  // Fermée, la section ne cache pas ses enfants : elle les DÉMONTE. Un
  // message d'erreur de validation vit dans ces enfants ; sans cette
  // ouverture, « Enregistrer » resterait sans effet visible et sans
  // explication. On ouvre seulement — jamais l'inverse — pour que le client
  // garde la main sur ce qu'il veut replier.
  useEffect(() => {
    if (forceOpen) setOpen(true)
  }, [forceOpen])

  return (
    <div className="rounded-lg border border-gray-200 dark:border-slate-700">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors rounded-lg"
      >
        <ChevronRight
          className={cn(
            'h-4 w-4 flex-shrink-0 text-gray-400 transition-transform',
            open && 'rotate-90',
          )}
        />
        <span className="text-sm font-medium text-gray-900 dark:text-white">
          {title}
        </span>
        {!open && summary && (
          <span className="ml-auto text-xs text-gray-500 dark:text-slate-400 truncate max-w-[55%] text-right">
            {summary}
          </span>
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-4 border-t border-gray-100 dark:border-slate-800">
          {children}
        </div>
      )}
    </div>
  )
}
